import * as XLSX from 'xlsx'
import ExcelJS from 'exceljs'
import {
  type StructureData,
  type Rights,
  type Group,
  type Drive,
  type Folder,
  type Permission,
  type Responsible,
  type Share,
  type Server,
  RIGHTS_OPTIONS,
  UNRESOLVED_EMPLOYEE_ID,
} from './types.ts'

/**
 * Flat representation einer Zugriffsmatrix-Zeile: genau EIN Ordner mit
 * Laufwerksbuchstabe, Ebene-1/Ebene-2-Namen, Verantwortlichen und einer
 * Abteilungs → Rights Map.
 */
export interface MatrixRow {
  folderId: string
  shareId?: string
  driveLetter: string
  driveLabel: string
  serverName: string
  shareName: string
  driveId?: string
  level1: string
  level2: string
  responsibleEmployees: Responsible[]
  rights: Record<string, Rights>
}

const FIXED_HEADERS = [
  'Laufwerk',
  'Benennung',
  'Server',
  'Share',
  'SubPath',
  'Ebene 1',
  'Ebene 2',
  'Verantwortlich',
  'Vertretung',
] as const

const RIGHT_BY_SHORT = new Map<string, Rights>(
  RIGHTS_OPTIONS.map((o) => [o.short.toUpperCase(), o.value]),
)
const RIGHT_BY_LABEL = new Map<string, Rights>(
  RIGHTS_OPTIONS.map((o) => [o.label.toLowerCase(), o.value]),
)

/** Parse string from Excel cell into Rights value. Akzeptiert Short-Codes (FC, M, R, …) oder volle Labels. */
export function parseRightsCell(raw: unknown): Rights | null {
  if (raw == null) return null
  const s = String(raw).trim()
  if (!s) return null
  if (s === '-') return 'Revoke'
  if (s === '—') return 'None'
  const upper = s.toUpperCase()
  if (upper === 'REVOKE' || upper === 'ENTZIEHEN' || upper === 'ENTZOGEN') return 'Revoke'
  if (RIGHT_BY_SHORT.has(upper)) return RIGHT_BY_SHORT.get(upper)!
  const lower = s.toLowerCase()
  if (RIGHT_BY_LABEL.has(lower)) return RIGHT_BY_LABEL.get(lower)!
  if (upper === 'X' || upper === 'VOLLZUGRIFF') return 'FullControl'
  if (upper === 'RW' || upper === 'ÄNDERN' || upper === 'AENDERN') return 'Modify'
  if (upper === 'LESEN') return 'Read'
  if (upper === 'DENY' || upper === 'NONE' || upper === 'KEIN' || upper === 'KEIN ZUGRIFF') return 'None'
  return null
}

/**
 * Erzeugt aus Folders + Permissions die flache Matrix-Zeilenliste.
 */
export function buildMatrixRows(data: StructureData): MatrixRow[] {
  const folderById = new Map(data.folders.map((f) => [f.id, f]))
  const driveById = new Map(data.drives.map((d) => [d.id, d]))
  const shareById = new Map<string, { share: Share; serverName: string }>()
  for (const s of data.servers) {
    for (const sh of s.shares) shareById.set(sh.id, { share: sh, serverName: s.name })
  }

  const rows: MatrixRow[] = []

  const shareRootPermsByShare = new Map<string, Record<string, Rights>>()
  for (const p of data.permissions) {
    if (p.folderId || !p.shareId) continue
    const rights = shareRootPermsByShare.get(p.shareId) ?? {}
    rights[p.groupId] = p.rights
    shareRootPermsByShare.set(p.shareId, rights)
  }
  const shareIdsWithRow = new Set<string>(shareRootPermsByShare.keys())
  for (const s of data.servers) {
    for (const sh of s.shares) {
      if (sh.responsibleEmployees && sh.responsibleEmployees.length > 0) shareIdsWithRow.add(sh.id)
    }
  }
  for (const shareId of shareIdsWithRow) {
    const shareInfo = shareById.get(shareId)
    if (!shareInfo) continue
    const drive = data.drives.find((d) => d.shareId === shareId)
    rows.push({
      folderId: '',
      shareId,
      driveLetter: drive?.letter ?? '',
      driveLabel: drive?.label ?? '',
      serverName: shareInfo.serverName,
      shareName: shareInfo.share.name,
      driveId: drive?.id,
      level1: '',
      level2: '',
      responsibleEmployees: shareInfo.share.responsibleEmployees ?? [],
      rights: shareRootPermsByShare.get(shareId) ?? {},
    })
  }

  for (const f of data.folders) {
    let parent: Folder | null = f.parent ? folderById.get(f.parent) ?? null : null
    if (parent && parent.parent) continue
    const level1 = parent ? parent.name : f.name
    const level2 = parent ? f.name : ''

    let driveId = f.driveId ?? parent?.driveId
    if (!driveId) {
      const candidate = data.drives.find((d) => d.shareId === f.shareId)
      driveId = candidate?.id
    }
    const drive = driveId ? driveById.get(driveId) : undefined
    const shareInfo = shareById.get(f.shareId)

    const rights: Record<string, Rights> = {}
    for (const p of data.permissions) {
      if (p.folderId === f.id) rights[p.groupId] = p.rights
    }

    // For Excel export: inherit responsible employees from parent when
    // the folder has none of its own — so each row in the Excel always
    // shows the effective responsible employees.
    const effectiveResponsibles =
      f.responsibleEmployees && f.responsibleEmployees.length > 0
        ? f.responsibleEmployees
        : parent?.responsibleEmployees ?? []

    rows.push({
      folderId: f.id,
      driveLetter: drive?.letter ?? '',
      driveLabel: drive?.label ?? '',
      serverName: shareInfo?.serverName ?? '',
      shareName: shareInfo?.share.name ?? '',
      driveId: drive?.id,
      level1,
      level2,
      responsibleEmployees: effectiveResponsibles,
      rights,
    })
  }

  rows.sort((a, b) => {
    const la = (a.driveLetter || 'Z').localeCompare(b.driveLetter || 'Z')
    if (la !== 0) return la
    const l1 = a.level1.localeCompare(b.level1, 'de', { sensitivity: 'base' })
    if (l1 !== 0) return l1
    if (!a.level2 && b.level2) return -1
    if (a.level2 && !b.level2) return 1
    return a.level2.localeCompare(b.level2, 'de', { sensitivity: 'base' })
  })

  return rows
}

/**
 * Short code → ARGB background color for styled export.
 * Palette matches the on-screen matrix:
 *   FC = Orange (full access),  M = Green (modify),
 *   R/RX/L = Yellow (read),  W = Amber (write),
 *   DENY = Red,  Revoke = Gray.
 */
const RIGHTS_FILL: Record<string, { bg: string; fg: string }> = {
  FC:   { bg: 'FFFB923C', fg: 'FF1F2937' }, // orange-400
  M:    { bg: 'FF86EFAC', fg: 'FF1F2937' }, // green-300
  RX:   { bg: 'FFFDE047', fg: 'FF1F2937' }, // yellow-300
  R:    { bg: 'FFFDE047', fg: 'FF1F2937' },
  W:    { bg: 'FFFCD34D', fg: 'FF1F2937' }, // amber-300
  L:    { bg: 'FFFEF08A', fg: 'FF1F2937' }, // yellow-200
  DENY: { bg: 'FFFCA5A5', fg: 'FF1F2937' }, // red-300
  '-':  { bg: 'FFE5E7EB', fg: 'FF6B7280' }, // gray-200
}

const FIXED_WIDTHS = [8, 22, 14, 14, 18, 24, 24, 22, 22] as const
const DEPT_WIDTH = 5

function thinBorder() {
  return {
    top:    { style: 'thin' as const, color: { argb: 'FFCBD5E1' } },
    bottom: { style: 'thin' as const, color: { argb: 'FFCBD5E1' } },
    left:   { style: 'thin' as const, color: { argb: 'FFCBD5E1' } },
    right:  { style: 'thin' as const, color: { argb: 'FFCBD5E1' } },
  }
}

/**
 * Builds a styled workbook with:
 *  - Frozen header region (2 rows + 9 columns)
 *  - Merged "Abteilungen" banner above the rights columns
 *  - 90°-rotated department headers
 *  - Colored rights cells (FC/M/R/...), drive section highlighted in gray
 *  - Legend on a second tab
 */
async function buildStyledWorkbook(
  header: string[],
  deptHeaders: string[],
  body: Array<{
    driveLetter: string
    driveLabel: string
    serverName: string
    shareName: string
    subPath: string
    level1: string
    level2: string
    responsibleName: string
    deputyName: string
    rightsShort: string[]
    isDriveRoot: boolean
  }>,
  titleHint: string,
): Promise<ArrayBuffer> {
  const wb = new ExcelJS.Workbook()
  wb.creator = 'SystemhausManager'
  wb.created = new Date()

  const ws = wb.addWorksheet('Zugriffsmatrix', {
    views: [{ state: 'frozen', xSplit: 9, ySplit: 2 }],
    properties: { defaultRowHeight: 18 },
  })

  const fixedCount = header.length
  const totalCols = fixedCount + deptHeaders.length

  // Column widths
  for (let c = 1; c <= totalCols; c++) {
    const col = ws.getColumn(c)
    col.width = c <= fixedCount
      ? FIXED_WIDTHS[c - 1] ?? 14
      : DEPT_WIDTH
  }

  // Row 1: Merged banner. First 9 columns = title, then = "Departments"
  const titleRow = ws.getRow(1)
  titleRow.height = 24
  ws.mergeCells(1, 1, 1, fixedCount)
  const titleCell = ws.getCell(1, 1)
  titleCell.value = titleHint
  titleCell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 12 }
  titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E3A8A' } }
  titleCell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 }
  if (deptHeaders.length > 0) {
    ws.mergeCells(1, fixedCount + 1, 1, totalCols)
    const deptBanner = ws.getCell(1, fixedCount + 1)
    deptBanner.value = 'Abteilungen'
    deptBanner.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 12 }
    deptBanner.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F766E' } }
    deptBanner.alignment = { horizontal: 'center', vertical: 'middle' }
  }

  // Row 2: Column headers
  const headerRow = ws.getRow(2)
  headerRow.height = 110
  for (let c = 1; c <= fixedCount; c++) {
    const cell = headerRow.getCell(c)
    cell.value = header[c - 1]
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } }
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF334155' } }
    cell.alignment = { horizontal: 'left', vertical: 'bottom', wrapText: true, indent: 1 }
    cell.border = thinBorder()
  }
  for (let i = 0; i < deptHeaders.length; i++) {
    const cell = headerRow.getCell(fixedCount + 1 + i)
    cell.value = deptHeaders[i]
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 }
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F766E' } }
    cell.alignment = { textRotation: 90, vertical: 'bottom', horizontal: 'center', wrapText: false }
    cell.border = thinBorder()
  }

  // Data rows
  let rowIdx = 3
  for (const r of body) {
    const row = ws.getRow(rowIdx)
    const values: Array<string> = [
      r.driveLetter, r.driveLabel, r.serverName, r.shareName,
      r.subPath, r.level1, r.level2, r.responsibleName, r.deputyName,
      ...r.rightsShort,
    ]
    for (let c = 1; c <= totalCols; c++) {
      const cell = row.getCell(c)
      cell.value = values[c - 1] ?? ''
      cell.border = thinBorder()
      if (c <= fixedCount) {
        cell.alignment = { vertical: 'middle', horizontal: c === 1 ? 'center' : 'left', indent: c === 1 ? 0 : 1 }
        if (r.isDriveRoot) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E7FF' } }
          cell.font = { bold: true, color: { argb: 'FF1E3A8A' } }
        } else if (rowIdx % 2 === 0) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } }
        }
      } else {
        const short = String(values[c - 1] ?? '').trim().toUpperCase()
        cell.alignment = { horizontal: 'center', vertical: 'middle' }
        const style = RIGHTS_FILL[short]
        if (style) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: style.bg } }
          cell.font = { bold: true, color: { argb: style.fg }, size: 10 }
        } else if (r.isDriveRoot) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E7FF' } }
        }
      }
    }
    row.height = r.isDriveRoot ? 22 : 18
    rowIdx++
  }

  // Auto-filter across header row
  ws.autoFilter = {
    from: { row: 2, column: 1 },
    to:   { row: 2, column: totalCols },
  }

  // ── Legend ────────────────────────────────────────────────
  const wsLegend = wb.addWorksheet('Legende', {
    views: [{ showGridLines: false }],
  })
  wsLegend.getColumn(1).width = 10
  wsLegend.getColumn(2).width = 32
  wsLegend.getColumn(3).width = 60

  wsLegend.mergeCells(1, 1, 1, 3)
  const lTitle = wsLegend.getCell(1, 1)
  lTitle.value = 'Zugriffsmatrix — Legende'
  lTitle.font = { bold: true, size: 14, color: { argb: 'FFFFFFFF' } }
  lTitle.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E3A8A' } }
  lTitle.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 }
  wsLegend.getRow(1).height = 28

  const lHead = wsLegend.getRow(3)
  lHead.values = ['Kürzel', 'Bedeutung', 'Farbe']
  lHead.font = { bold: true, color: { argb: 'FFFFFFFF' } }
  lHead.eachCell((c) => {
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF334155' } }
    c.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 }
    c.border = thinBorder()
  })

  let lr = 4
  for (const o of RIGHTS_OPTIONS) {
    const row = wsLegend.getRow(lr++)
    row.getCell(1).value = o.short
    row.getCell(2).value = o.label
    row.getCell(3).value = ''
    const style = RIGHTS_FILL[o.short]
    for (let c = 1; c <= 3; c++) {
      const cell = row.getCell(c)
      cell.border = thinBorder()
      cell.alignment = { vertical: 'middle', horizontal: c === 1 ? 'center' : 'left', indent: c === 1 ? 0 : 1 }
    }
    if (style) {
      const colorCell = row.getCell(3)
      colorCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: style.bg } }
    }
    row.getCell(1).font = { bold: true }
  }

  lr++
  const hintRow = wsLegend.getRow(lr++)
  hintRow.getCell(1).value = 'Hinweis'
  hintRow.getCell(1).font = { bold: true }
  wsLegend.mergeCells(lr - 1, 2, lr - 1, 3)
  hintRow.getCell(2).value = 'Leere Zelle = keine direkte Berechtigung (ggf. vom Parent-Ordner geerbt).'
  hintRow.getCell(2).alignment = { vertical: 'middle', horizontal: 'left', indent: 1, wrapText: true }

  const tipRow = wsLegend.getRow(lr++)
  tipRow.getCell(1).value = 'Import'
  tipRow.getCell(1).font = { bold: true }
  wsLegend.mergeCells(lr - 1, 2, lr - 1, 3)
  tipRow.getCell(2).value = 'Speichern, danach zurück in den SystemhausManager — Excel-Datei einfach per Drag & Drop auf die Matrix ziehen.'
  tipRow.getCell(2).alignment = { vertical: 'middle', horizontal: 'left', indent: 1, wrapText: true }

  return wb.xlsx.writeBuffer() as Promise<ArrayBuffer>
}

function downloadBuffer(buf: ArrayBuffer, filename: string) {
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/**
 * Exports the current structure as a styled .xlsx file.
 * Format: Drive, Label, Server, Share, SubPath, Level 1, Level 2,
 * Responsible, Deputy, [Departments].
 */
export async function exportMatrixToExcel(
  data: StructureData,
  abGroups: Group[],
  filenameHint: string,
): Promise<void> {
  const rows = buildMatrixRows(data)
  const deptHeaders = abGroups.map((g) => g.teamName?.trim() || g.name)

  const driveById = new Map(data.drives.map((d) => [d.id, d]))
  const body = rows.map((r) => {
    const rightsShort = abGroups.map((g) => {
      const rights = r.rights[g.id]
      if (!rights) return ''
      return RIGHTS_OPTIONS.find((o) => o.value === rights)?.short ?? ''
    })
    const subPath = r.driveId ? (driveById.get(r.driveId)?.subPath ?? '') : ''
    // Excel columns "Responsible" + "Deputy" from the ordered list:
    //   Position 1 → Responsible, Position 2+3 → Deputy (comma-separated)
    const respPrimary = r.responsibleEmployees[0]?.name ?? ''
    const respDeputy = r.responsibleEmployees.slice(1).map((e) => e.name).join(', ')
    return {
      driveLetter: r.driveLetter,
      driveLabel: r.driveLabel,
      serverName: r.serverName,
      shareName: r.shareName,
      subPath,
      level1: r.level1,
      level2: r.level2,
      responsibleName: respPrimary,
      deputyName: respDeputy,
      rightsShort,
      isDriveRoot: !r.level1 && !r.level2,
    }
  })

  const safeName = filenameHint.replace(/[^\w.\-]+/g, '_').replace(/^_+|_+$/g, '') || 'zugriffsmatrix'
  const date = new Date().toISOString().slice(0, 10)
  const buf = await buildStyledWorkbook(
    [...FIXED_HEADERS],
    deptHeaders,
    body,
    `Zugriffsmatrix · ${filenameHint}`,
  )
  downloadBuffer(buf, `${safeName}_Zugriffsmatrix_${date}.xlsx`)
}

/**
 * Exports a template with example rows and example departments,
 * so the user can immediately see what a valid import file looks like.
 */
export async function exportMatrixTemplate(filenameHint = 'Vorlage'): Promise<void> {
  const deptHeaders = ['Geschäftsführung', 'Buchhaltung', 'Vertrieb', 'IT', 'Produktion']

  const body: Parameters<typeof buildStyledWorkbook>[2] = [
    {
      driveLetter: 'P', driveLabel: 'Projekte', serverName: 'FS01', shareName: 'Projekte',
      subPath: '', level1: '', level2: '',
      responsibleName: 'M. Müller', deputyName: 'A. Schmitz',
      rightsShort: ['FC', 'R', 'R', 'M', 'R'],
      isDriveRoot: true,
    },
    {
      driveLetter: '', driveLabel: '', serverName: '', shareName: '',
      subPath: '', level1: 'Angebote', level2: '',
      responsibleName: 'M. Müller', deputyName: 'A. Schmitz',
      rightsShort: ['FC', 'R', 'M', '', ''],
      isDriveRoot: false,
    },
    {
      driveLetter: '', driveLabel: '', serverName: '', shareName: '',
      subPath: '', level1: 'Angebote', level2: 'Entwürfe',
      responsibleName: 'A. Schmitz', deputyName: '',
      rightsShort: ['FC', 'R', 'M', '', ''],
      isDriveRoot: false,
    },
    {
      driveLetter: '', driveLabel: '', serverName: '', shareName: '',
      subPath: '', level1: 'Rechnungen', level2: '',
      responsibleName: 'F. Weber', deputyName: 'S. Klein',
      rightsShort: ['FC', 'M', '', '', ''],
      isDriveRoot: false,
    },
    {
      driveLetter: 'B', driveLabel: 'Buchhaltung', serverName: 'FS01', shareName: 'Buchhaltung',
      subPath: '', level1: '', level2: '',
      responsibleName: 'F. Weber', deputyName: 'S. Klein',
      rightsShort: ['FC', 'FC', 'DENY', 'R', 'DENY'],
      isDriveRoot: true,
    },
    {
      driveLetter: '', driveLabel: '', serverName: '', shareName: '',
      subPath: '', level1: 'Lohnbuchhaltung', level2: '',
      responsibleName: 'F. Weber', deputyName: '',
      rightsShort: ['FC', 'M', 'DENY', '-', 'DENY'],
      isDriveRoot: false,
    },
    {
      driveLetter: 'T', driveLabel: 'Technik', serverName: 'FS02', shareName: 'Technik',
      subPath: '', level1: '', level2: '',
      responsibleName: 'J. Becker', deputyName: 'T. Lange',
      rightsShort: ['R', '', 'R', 'FC', 'M'],
      isDriveRoot: true,
    },
    {
      driveLetter: '', driveLabel: '', serverName: '', shareName: '',
      subPath: '', level1: 'Dokumentation', level2: '',
      responsibleName: 'J. Becker', deputyName: '',
      rightsShort: ['R', 'R', 'R', 'FC', 'R'],
      isDriveRoot: false,
    },
    {
      driveLetter: '', driveLabel: '', serverName: '', shareName: '',
      subPath: '', level1: 'Dokumentation', level2: 'Intern',
      responsibleName: 'T. Lange', deputyName: '',
      rightsShort: ['', '', '', 'FC', 'R'],
      isDriveRoot: false,
    },
    {
      driveLetter: '', driveLabel: '', serverName: '', shareName: '',
      subPath: '', level1: 'Maschinen', level2: 'Anleitungen',
      responsibleName: 'J. Becker', deputyName: 'T. Lange',
      rightsShort: ['R', '', '', 'M', 'FC'],
      isDriveRoot: false,
    },
  ]

  const buf = await buildStyledWorkbook(
    [...FIXED_HEADERS],
    deptHeaders,
    body,
    'Vorlage · Zugriffsmatrix',
  )
  const safeName = filenameHint.replace(/[^\w.\-]+/g, '_').replace(/^_+|_+$/g, '') || 'Vorlage'
  downloadBuffer(buf, `${safeName}_Zugriffsmatrix.xlsx`)
}

/**
 * Parsed row from Excel/CSV – includes all columns from the user's Excel format.
 */
export interface ParsedImport {
  rows: Array<{
    driveLetter: string
    driveLabel: string
    serverName: string
    shareName: string
    subPath: string
    level1: string
    level2: string
    level3: string
    level4: string
    responsibleName: string
    deputyName: string
    rights: Record<string, string>
  }>
  departmentColumns: string[]
  filename: string
  /** Diagnostic info: for the import preview modal */
  debug: {
    headerRowIdx: number
    columnsFound: Record<string, number>
    rawHeaderCells: string[]
    rawFirstDataCells: string[]
    sampleRows: Array<{ driveLetter: string; serverName: string; shareName: string; level1: string; level2: string }>
  }
}

export async function parseMatrixFile(file: File): Promise<ParsedImport> {
  const buf = await file.arrayBuffer()
  const wb = XLSX.read(buf, { type: 'array' })
  const sheetName = wb.SheetNames.find((n: string) => /zugriffs|matrix|acl/i.test(n)) ?? wb.SheetNames[0]
  if (!sheetName) throw new Error('Keine Tabellenblätter in der Datei.')
  const sheet = wb.Sheets[sheetName]
  const aoa: unknown[][] = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    raw: false,
    defval: '',
  }) as unknown[][]
  return parseSheetAoA(aoa, file.name.replace(/\.[^.]+$/, ''))
}

/**
 * Shared parser for header/body logic.
 *
 * Special features:
 * - Auto-detect of the header row (searches the first 10 rows for "Laufwerk" / "Ebene 1")
 * - Carry-forward: Drive, Label, Server, Share often appear only in the first row
 *   of a group — subsequent empty cells inherit the last seen value.
 * - Implied parent: If Level 1 is empty but Level 2 is filled, the folder is treated
 *   as a child of the last seen Level 1.
 */
function parseSheetAoA(aoa: unknown[][], filename: string): ParsedImport {
  if (aoa.length < 1) throw new Error('Leere Tabelle.')

  // 1. Score-based header row detection across first 10 rows.
  // Handles multi-row Excel headers where "Laufwerk"/"Benennung" are in merged
  // cells above the actual data header that contains "Server", "Share" etc.
  // The row with the highest score is used as the data header.
  let headerRowIdx = 0
  let bestScore = -1
  for (let i = 0; i < Math.min(10, aoa.length); i++) {
    const cells = (aoa[i] ?? []).map((c) => String(c ?? '').trim().toLowerCase())
    let score = 0
    if (cells.some((c) => c === 'laufwerk' || c === 'drive')) score += 10
    if (cells.some((c) => c === 'server' || c === 'servername' || c === 'dateiserver')) score += 8
    if (cells.some((c) => c === 'share' || c === 'freigabe' || c === 'freigabename' || c === 'share-name')) score += 8
    if (cells.some((c) => c === 'ebene 1' || c === 'level 1' || c === 'ebene1' || c === 'level1')) score += 5
    if (cells.some((c) => c === 'ebene 2' || c === 'level 2' || c === 'ebene2' || c === 'level2')) score += 3
    if (cells.some((c) => c === 'verantwortlich' || c === 'verantwortlich haupt' || c === 'responsible')) score += 3
    if (cells.some((c) => c === 'vertretung' || c === 'deputy' || c === 'stellvertretung')) score += 2
    if (score > bestScore) {
      bestScore = score
      headerRowIdx = i
    }
  }

  // Build column name → index map from ALL rows 0..headerRowIdx.
  // Later rows take precedence: the actual data header row overrides merged
  // group labels above it (e.g. "Ebene 1" as group label in row 0 is
  // overwritten by "Ebene 1" as the actual column header in row 2).
  const indexByHeader = new Map<string, number>()
  for (let ri = 0; ri <= headerRowIdx; ri++) {
    const row = aoa[ri] ?? []
    for (let ci = 0; ci < row.length; ci++) {
      const v = String(row[ci] ?? '').trim()
      if (v) indexByHeader.set(v.toLowerCase(), ci)
    }
  }

  function col(...names: string[]): number {
    for (const name of names) {
      const idx = indexByHeader.get(name.toLowerCase())
      if (idx !== undefined) return idx
    }
    return -1
  }

  const iLetter  = col('Laufwerk', 'Drive', 'LW', 'Buchstabe')
  const iLabel   = col('Benennung', 'Label', 'Bezeichnung', 'Laufwerk-Benennung')
  const iServer  = col('Server', 'Servername', 'Dateiserver')
  const iShare   = col('Share', 'Freigabe', 'Freigabename', 'Share-Name')
  const iSubPath = col('SubPath', 'Unterpfad', 'Sub-Pfad', 'Unterordner')
  const iL1      = col('Ebene 1', 'Level 1', 'Ordner 1', 'Level1', 'Ebene1')
  const iL2      = col('Ebene 2', 'Level 2', 'Ordner 2', 'Level2', 'Ebene2')
  const iL3      = col('Ebene 3', 'Level 3', 'Ordner 3', 'Level3', 'Ebene3')
  const iL4      = col('Ebene 4', 'Level 4', 'Ordner 4', 'Level4', 'Ebene4')
  const iResp    = col('Verantwortlich Haupt', 'Verantwortlich', 'Responsible', 'Hauptverantwortlich')
  const iDep     = col('Verantwortlich Sekundär', 'Vertretung', 'Deputy', 'Stellvertretung')

  if (iL1 === -1 && iL2 === -1) throw new Error(
    'Spalte "Ebene 1" nicht gefunden. Bitte sicherstellen, dass die Datei eine Header-Zeile mit ' +
    '"Laufwerk", "Ebene 1", "Ebene 2", "Verantwortlich", "Vertretung" enthält.',
  )

  const fixedColumns = new Set(
    [iLetter, iLabel, iServer, iShare, iSubPath, iL1, iL2, iL3, iL4, iResp, iDep].filter((x) => x >= 0),
  )

  // Collect department columns: dept names often appear in the FIRST row of
  // merged cell regions, while the header row below is empty or contains
  // sub-labels (e.g. "1. Team"). Therefore FIRST-OCCURRENCE wins:
  // earlier rows set the name, later rows (sub-labels) do not overwrite it.
  const deptNameByIdx = new Map<number, string>()
  for (let ri = 0; ri <= headerRowIdx; ri++) {
    const r = aoa[ri] ?? []
    for (let ci = 0; ci < r.length; ci++) {
      if (fixedColumns.has(ci)) continue
      if (deptNameByIdx.has(ci)) continue  // first occurrence wins — sub-labels don't overwrite main names
      const v = String(r[ci] ?? '').trim()
      if (v) deptNameByIdx.set(ci, v)
    }
  }
  const departmentColumns: { idx: number; name: string }[] = []
  for (const [idx, name] of deptNameByIdx.entries()) {
    departmentColumns.push({ idx, name })
  }
  departmentColumns.sort((a, b) => a.idx - b.idx)

  // 2. Carry-forward state
  let prevLetter = ''
  let prevLabel  = ''
  let prevServer = ''
  let prevShare  = ''
  let prevLevel1 = ''
  let prevLevel2 = ''
  let prevLevel3 = ''

  const rows: ParsedImport['rows'] = []

  for (let r = headerRowIdx + 1; r < aoa.length; r++) {
    const row = aoa[r]
    if (!row || row.every((c) => !c || !String(c).trim())) continue

    const rawLetter = iLetter >= 0 ? String(row[iLetter] ?? '').trim() : ''
    const rawLabel  = iLabel  >= 0 ? String(row[iLabel]  ?? '').trim() : ''
    const rawServer = iServer >= 0 ? String(row[iServer] ?? '').trim() : ''
    const rawShare  = iShare  >= 0 ? String(row[iShare]  ?? '').trim() : ''

    // New values overwrite the stored value, empty cells inherit
    if (rawLetter) prevLetter = rawLetter
    if (rawLabel)  prevLabel  = rawLabel
    if (rawServer) prevServer = rawServer
    if (rawShare)  prevShare  = rawShare

    // Drive letter: "P:" → "P"
    const driveLetter = prevLetter.replace(/:/g, '').toUpperCase().slice(0, 1)

    const level1Raw = iL1 >= 0 ? String(row[iL1] ?? '').trim() : ''
    const level2Raw = iL2 >= 0 ? String(row[iL2] ?? '').trim() : ''
    const level3Raw = iL3 >= 0 ? String(row[iL3] ?? '').trim() : ''
    const level4Raw = iL4 >= 0 ? String(row[iL4] ?? '').trim() : ''

    // Carry-forward: remember higher level; new higher level resets deeper levels
    if (level1Raw) { prevLevel1 = level1Raw; prevLevel2 = ''; prevLevel3 = '' }
    if (level2Raw) { prevLevel2 = level2Raw; prevLevel3 = '' }
    else if (level1Raw) { prevLevel2 = '' }
    if (level3Raw) { prevLevel3 = level3Raw }
    else if (level2Raw || level1Raw) { prevLevel3 = '' }

    // Implied parent: empty cell inherits the last seen value of the parent level
    const level1 = level1Raw || (level2Raw || level3Raw || level4Raw ? prevLevel1 : '')
    const level2 = level2Raw || (level3Raw || level4Raw ? prevLevel2 : '')
    const level3 = level3Raw || (level4Raw ? prevLevel3 : '')
    const level4 = level4Raw

    if (!level1 && !level2 && !level3 && !level4 && !driveLetter) continue

    const rights: Record<string, string> = {}
    for (const d of departmentColumns) {
      rights[d.name] = String(row[d.idx] ?? '').trim()
    }

    rows.push({
      driveLetter,
      driveLabel: prevLabel,
      serverName: prevServer,
      shareName:  prevShare,
      subPath:    iSubPath >= 0 ? String(row[iSubPath] ?? '').trim() : '',
      level1,
      level2,
      level3,
      level4,
      responsibleName: iResp >= 0 ? String(row[iResp] ?? '').trim() : '',
      deputyName:      iDep  >= 0 ? String(row[iDep]  ?? '').trim() : '',
      rights,
    })
  }

  return {
    rows,
    departmentColumns: departmentColumns.map((d) => d.name),
    filename,
    debug: {
      headerRowIdx,
      columnsFound: {
        Laufwerk: iLetter, Benennung: iLabel, Server: iServer, Share: iShare,
        SubPath: iSubPath, 'Ebene 1': iL1, 'Ebene 2': iL2, 'Ebene 3': iL3, 'Ebene 4': iL4,
        Verantwortlich: iResp, Vertretung: iDep,
        [`Abteilungen (${departmentColumns.length})`]: departmentColumns.length > 0 ? 0 : -1,
      },
      rawHeaderCells: (aoa[headerRowIdx] ?? []).slice(0, 16).map((c) => String(c ?? '').trim()),
      rawFirstDataCells: (aoa[headerRowIdx + 1] ?? []).slice(0, 16).map((c) => String(c ?? '').trim()),
      sampleRows: rows.slice(0, 5).map((r) => ({
        driveLetter: r.driveLetter, serverName: r.serverName,
        shareName: r.shareName, level1: r.level1, level2: r.level2,
      })),
    },
  }
}

/**
 * Normalizes names for comparison: resolve umlauts, remove special characters,
 * ignore case.
 */
function normalizeName(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '')
}

export interface ImportSummary {
  rowsImported: number
  serversCreated: number
  sharesCreated: number
  foldersCreated: number
  foldersUpdated: number
  drivesCreated: number
  groupsCreated: number
  permissionsSet: number
  warnings: string[]
}

/**
 * Applies a parsed import to StructureData.
 *
 * Strategy:
 * - Server+Share are matched by name (normalized, case-insensitive).
 * - Drive letter is matched by letter+shareId or created anew.
 * - Label (drive label) is set if still empty.
 * - Level 1 = root folder, Level 2 = child, Level 3 = sub-child (3-level hierarchy).
 * - Unknown department column names create new AB groups.
 */
export function applyImport(
  data: StructureData,
  parsed: ParsedImport,
  options: { replaceExisting: boolean } = { replaceExisting: false },
): { patch: Partial<StructureData>; summary: ImportSummary } {
  const summary: ImportSummary = {
    rowsImported: 0,
    serversCreated: 0,
    sharesCreated: 0,
    foldersCreated: 0,
    foldersUpdated: 0,
    drivesCreated: 0,
    groupsCreated: 0,
    permissionsSet: 0,
    warnings: [],
  }

  // Mutable copies of all collections
  const servers: Server[] = data.servers.map((s) => ({ ...s, shares: [...s.shares] }))
  const drives: Drive[] = [...data.drives]
  const folders: Folder[] = options.replaceExisting ? [] : [...data.folders]
  const groups: Group[] = [...data.groups]
  const permissions: Permission[] = options.replaceExisting ? [] : [...data.permissions]

  // Match server + share by name or create new
  function findOrCreateShare(serverName: string, shareName: string): string {
    const nServer = normalizeName(serverName)
    const nShare  = normalizeName(shareName)

    if (nServer) {
      for (const s of servers) {
        if (normalizeName(s.name) !== nServer) continue
        if (nShare) {
          const sh = s.shares.find((x) => normalizeName(x.name) === nShare)
          if (sh) return sh.id
          // Server found, share not → create share
          const newShare: Share = {
            id: crypto.randomUUID(),
            name: shareName,
            isHidden: false,
            uncPath: `\\\\${serverName}\\${shareName}`,
          }
          s.shares.push(newShare)
          summary.sharesCreated++
          return newShare.id
        }
        if (s.shares.length > 0) return s.shares[0].id
      }
      // Server not found → create server + share
      const newShare: Share = {
        id: crypto.randomUUID(),
        name: shareName || 'Share',
        isHidden: false,
        uncPath: `\\\\${serverName}\\${shareName || 'Share'}`,
      }
      const newServer: Server = {
        id: crypto.randomUUID(),
        name: serverName,
        hostname: serverName,
        shares: [newShare],
      }
      servers.push(newServer)
      summary.serversCreated++
      summary.sharesCreated++
      return newShare.id
    }

    if (nShare) {
      for (const s of servers) {
        const sh = s.shares.find((x) => normalizeName(x.name) === nShare)
        if (sh) return sh.id
      }
    }

    // Fallback: first available share
    return servers.flatMap((s) => s.shares)[0]?.id ?? ''
  }

  // Match drive by letter + shareId or create new.
  // subPath logic:
  //   1. explicit SubPath column > 2. Label > 3. empty.
  //   Exception: if Label == Share name (e.g. W: | MaKs | MAKS | MaKs),
  //   the drive maps to the share root → subPath='' (not label!),
  //   otherwise an extra wrapper folder with the share name would be created.
  function findOrCreateDrive(
    letter: string,
    label: string,
    shareId: string,
    shareName: string,
    subPath?: string,
  ): Drive | undefined {
    if (!letter) return undefined
    const up = letter.toUpperCase()
    const labelMatchesShare =
      !!label && !!shareName && normalizeName(label) === normalizeName(shareName)
    const inferredFromLabel = labelMatchesShare ? '' : label
    const effectiveSubPath = subPath ?? inferredFromLabel ?? ''
    // First exact match (letter + share), then letter only
    let existing = drives.find((d) => d.letter.toUpperCase() === up && d.shareId === shareId)
    if (!existing) existing = drives.find((d) => d.letter.toUpperCase() === up)
    if (existing) {
      if (label && !existing.label) existing.label = label
      // Fill in subPath if still empty — e.g. drive from a previous import without subPath
      if (!existing.subPath && effectiveSubPath) existing.subPath = effectiveSubPath
      return existing
    }
    if (!shareId) return undefined
    const d: Drive = {
      id: crypto.randomUUID(),
      letter: up.slice(0, 1),
      shareId,
      label: label || '',
      subPath: effectiveSubPath,
      type: 'team',
    }
    drives.push(d)
    summary.drivesCreated++
    return d
  }

  // Group lookup by teamName or name (normalized)
  function findOrCreateGroup(headerName: string): Group {
    const n = normalizeName(headerName)
    const existing = groups.find((g) => g.type === 'AB' && (
      normalizeName(g.teamName ?? '') === n || normalizeName(g.name) === n
    ))
    if (existing) return existing
    const adName = headerName
      .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
      .replace(/Ä/g, 'Ae').replace(/Ö/g, 'Oe').replace(/Ü/g, 'Ue')
      .replace(/[^a-zA-Z0-9]+/g, '')
      || `Gruppe${groups.length + 1}`
    const g: Group = {
      id: crypto.randomUUID(),
      type: 'AB',
      name: adName,
      teamName: headerName,
      scope: 'global',
    }
    groups.push(g)
    summary.groupsCreated++
    return g
  }

  const groupByHeader = new Map<string, Group>()
  for (const h of parsed.departmentColumns) {
    groupByHeader.set(h, findOrCreateGroup(h))
  }

  // Root folder deduplication: (driveId, normalizedName) → Folder
  const rootIndex = new Map<string, Folder>()
  for (const f of folders) {
    if (!f.parent) rootIndex.set(`${f.driveId ?? ''}::${normalizeName(f.name)}`, f)
  }

  function ensureRootFolder(
    driveId: string | undefined,
    shareId: string,
    name: string,
    resp: Responsible[],
  ): Folder {
    const key = `${driveId ?? ''}::${normalizeName(name)}`
    const existing = rootIndex.get(key)
    if (existing) {
      if (resp.length > 0 && (!existing.responsibleEmployees || existing.responsibleEmployees.length === 0)) {
        existing.responsibleEmployees = resp
        summary.foldersUpdated++
      }
      return existing
    }
    const f: Folder = {
      id: crypto.randomUUID(),
      shareId,
      driveId,
      name,
      relativePath: name,
      parent: null,
      responsibleEmployees: resp.length > 0 ? resp : undefined,
    }
    folders.push(f)
    rootIndex.set(key, f)
    summary.foldersCreated++
    return f
  }

  function ensureChildFolder(
    parent: Folder,
    name: string,
    shareId: string,
    driveId: string | undefined,
    resp: Responsible[],
  ): Folder {
    const existing = folders.find(
      (f) => f.parent === parent.id && normalizeName(f.name) === normalizeName(name),
    )
    if (existing) {
      if (resp.length > 0 && (!existing.responsibleEmployees || existing.responsibleEmployees.length === 0)) {
        existing.responsibleEmployees = resp
        summary.foldersUpdated++
      }
      return existing
    }
    const f: Folder = {
      id: crypto.randomUUID(),
      shareId,
      driveId,
      name,
      relativePath: `${parent.relativePath}\\${name}`,
      parent: parent.id,
      responsibleEmployees: resp.length > 0 ? resp : undefined,
    }
    folders.push(f)
    summary.foldersCreated++
    return f
  }

  for (const row of parsed.rows) {
    summary.rowsImported++

    const shareId = findOrCreateShare(row.serverName, row.shareName)
    const drive = findOrCreateDrive(
      row.driveLetter,
      row.driveLabel,
      shareId,
      row.shareName,
      row.subPath || undefined,
    )
    const effectiveShareId = drive?.shareId ?? shareId

    const name1 = row.level1
    const name2 = row.level2
    const name3 = row.level3 ?? ''
    const name4 = row.level4 ?? ''
    // Excel fields "Responsible" / "Deputy" → Responsible list.
    // Without TANSS employee context here: `UNRESOLVED_EMPLOYEE_ID` marks
    // entries that the user must manually assign to an employee in the picker.
    // "Deputy" may contain multiple names comma-separated.
    const respData: Responsible[] = []
    if (row.responsibleName && row.responsibleName.trim()) {
      respData.push({ employeeId: UNRESOLVED_EMPLOYEE_ID, name: row.responsibleName.trim() })
    }
    if (row.deputyName && row.deputyName.trim()) {
      for (const part of row.deputyName.split(/[,;]/)) {
        const name = part.trim()
        if (name) respData.push({ employeeId: UNRESOLVED_EMPLOYEE_ID, name })
        if (respData.length >= 3) break
      }
    }

    // If the drive has a subPath (e.g. P: → \\Server\Share\CentralDepts),
    // a root folder with name=subPath is automatically created. All further
    // folders in this row are nested under this subPath root, so that
    // - drive.subPath === folder.relativePath holds for the root folder (→ UI
    //   recognizes it as the drive mount point)
    // - the filesystem hierarchy is correctly represented.
    const subPathRoot: Folder | undefined = drive?.subPath
      ? ensureRootFolder(drive.id, effectiveShareId, drive.subPath, [])
      : undefined

    // Row without folder entries (no Level 1/2/3/4):
    //   - with SubPath drive: permissions on subPath root folder
    //   - without SubPath drive: classic share-root permissions
    if (!name1 && !name2 && !name3 && !name4) {
      if (subPathRoot) {
        if (respData.length > 0 && (!subPathRoot.responsibleEmployees || subPathRoot.responsibleEmployees.length === 0)) {
          subPathRoot.responsibleEmployees = respData
          summary.foldersUpdated++
        }
        for (const [header, raw] of Object.entries(row.rights)) {
          const group = groupByHeader.get(header)
          if (!group) continue
          const rights = parseRightsCell(raw)
          const idx = permissions.findIndex(
            (p) => p.folderId === subPathRoot.id && p.groupId === group.id,
          )
          if (idx >= 0) permissions.splice(idx, 1)
          if (rights !== null) {
            permissions.push({ folderId: subPathRoot.id, groupId: group.id, rights })
            summary.permissionsSet++
          }
        }
        continue
      }
      if (!effectiveShareId) continue
      if (respData.length > 0) {
        for (const s of servers) {
          const sh = s.shares.find((x) => x.id === effectiveShareId)
          if (sh && (!sh.responsibleEmployees || sh.responsibleEmployees.length === 0)) {
            sh.responsibleEmployees = respData
          }
        }
      }
      for (const [header, raw] of Object.entries(row.rights)) {
        const group = groupByHeader.get(header)
        if (!group) continue
        const rights = parseRightsCell(raw)
        const idx = permissions.findIndex(
          (p) => !p.folderId && p.shareId === effectiveShareId && p.groupId === group.id,
        )
        if (idx >= 0) permissions.splice(idx, 1)
        if (rights !== null) {
          permissions.push({ shareId: effectiveShareId, groupId: group.id, rights })
          summary.permissionsSet++
        }
      }
      continue
    }

    // Build folder hierarchy
    const rootName = name1 || name2 || name3 || name4

    // Root folder: set responsible only if it is a leaf entry.
    // With a SubPath drive, Level 1 is created as a child of the subPath root.
    // Edge case: Level 1 == subPath name → use subPath root directly,
    // no double nesting.
    const isLeaf = !name2 && !name3 && !name4
    let root: Folder
    if (subPathRoot && normalizeName(rootName) === normalizeName(subPathRoot.name)) {
      root = subPathRoot
      if (isLeaf && respData.length > 0 && (!root.responsibleEmployees || root.responsibleEmployees.length === 0)) {
        root.responsibleEmployees = respData
        summary.foldersUpdated++
      }
    } else if (subPathRoot) {
      root = ensureChildFolder(subPathRoot, rootName, effectiveShareId, drive?.id, isLeaf ? respData : [])
    } else {
      root = ensureRootFolder(drive?.id, effectiveShareId, rootName, isLeaf ? respData : [])
    }

    let target: Folder = root

    // name2 set (name1 possibly via carry-forward)
    if (name2) {
      const isL2Leaf = !name3 && !name4
      const l2 = ensureChildFolder(root, name2, effectiveShareId, drive?.id, isL2Leaf ? respData : [])
      target = l2

      if (name3) {
        const isL3Leaf = !name4
        const l3 = ensureChildFolder(l2, name3, effectiveShareId, drive?.id, isL3Leaf ? respData : [])
        target = l3

        if (name4) {
          target = ensureChildFolder(l3, name4, effectiveShareId, drive?.id, respData)
        }
      } else if (name4) {
        // name3 empty, name4 set → name4 directly under l2
        target = ensureChildFolder(l2, name4, effectiveShareId, drive?.id, respData)
      }
    } else if (name3) {
      // name2 empty, name3 set → name3 directly under root
      const isL3Leaf = !name4
      const l3 = ensureChildFolder(root, name3, effectiveShareId, drive?.id, isL3Leaf ? respData : [])
      target = l3

      if (name4) {
        target = ensureChildFolder(l3, name4, effectiveShareId, drive?.id, respData)
      }
    } else if (name4) {
      // name2+name3 leer, name4 gesetzt → direkt unter root
      target = ensureChildFolder(root, name4, effectiveShareId, drive?.id, respData)
    }

    // Berechtigungen setzen
    for (const [header, raw] of Object.entries(row.rights)) {
      const group = groupByHeader.get(header)
      if (!group) continue
      const rights = parseRightsCell(raw)
      const idx = permissions.findIndex((p) => p.folderId === target.id && p.groupId === group.id)
      if (idx >= 0) permissions.splice(idx, 1)
      if (rights !== null) {
        permissions.push({ folderId: target.id, groupId: group.id, rights })
        summary.permissionsSet++
      }
    }
  }

  const patch: Partial<StructureData> = { servers, folders, drives, groups, permissions }
  return { patch, summary }
}
